import { FC, useState, useEffect, useRef } from "react";
import { Box, styled, Typography } from "@mui/material";
import { renderAsync } from "docx-preview";
import { black, background } from "@/theme/colors";
import { useZoomPan } from "@/features/talent/components/talent-scan-image/use-zoom-pan";
import { ZoomControls } from "@/features/talent/components/talent-scan-image/ZoomControls";
import { fonts } from "@/theme/typography";
import { OfficeFilePreview } from "@/features/talent/components/talent-scan-image/OfficeFilePreview";
import { DOCX_MIME_TYPE } from "@/features/talent/constants/scan-accept";
import { normalizeDocxSymbolBullets } from "@/features/talent/components/talent-scan-image/normalize-docx-symbol-bullets";

type TDocxPreviewWithZoomProps = {
  src: File;
  width?: number | string;
  height?: number;
};

export const DocxPreviewWithZoom: FC<TDocxPreviewWithZoomProps> = ({ src, width = "100%", height = 500 }) => {
  const [contentDimensions, setContentDimensions] = useState({ width: 0, height: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [hasFailed, setHasFailed] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const styleRef = useRef<HTMLDivElement>(null);

  const { scale, position, isDragging, zoomIn, zoomOut, fitToViewport, handlers } = useZoomPan({
    contentWidth: contentDimensions.width,
    contentHeight: contentDimensions.height,
    containerRef,
    fitOnLoad: true,
    // Width-only: fitting a long document's full height would shrink it to illegibility.
    fitMode: "width",
    enabled: !hasFailed,
  });

  // Render docx into an isolated container; docx-preview scopes all emitted CSS
  // under .docx*, so the injected stylesheet cannot leak into the host app.
  useEffect(() => {
    let isCancelled = false;

    const renderDocx = async () => {
      const contentEl = contentRef.current;
      const styleEl = styleRef.current;
      if (!src || !contentEl || !styleEl) return;

      setIsLoading(true);
      setHasFailed(false);
      setContentDimensions({ width: 0, height: 0 });
      contentEl.innerHTML = "";
      styleEl.innerHTML = "";

      // renderAsync only rejects on non-Word payloads via an unguarded null
      // deref inside the library, so gate on the declared mime instead.
      if (src.type !== DOCX_MIME_TYPE) {
        setHasFailed(true);
        setIsLoading(false);
        return;
      }

      try {
        await renderAsync(src, contentEl, styleEl, {
          className: "docx",
          inWrapper: true,
          breakPages: true,
          ignoreLastRenderedPageBreak: true,
        });
        // A newer src took over this container mid-render; drop stale output.
        if (isCancelled) return;

        normalizeDocxSymbolBullets(styleEl, fonts);

        setContentDimensions({
          width: contentEl.scrollWidth,
          height: contentEl.scrollHeight,
        });
        setIsLoading(false);
      } catch (error) {
        if (isCancelled) return;
        console.error("DOCX render failed:", error);
        contentEl.innerHTML = "";
        styleEl.innerHTML = "";
        setHasFailed(true);
        setIsLoading(false);
      }
    };

    renderDocx();

    return () => {
      isCancelled = true;
    };
  }, [src]);

  return (
    <PreviewContainer width={width} height={height} hasFailed={hasFailed}>
      <DocxViewport
        ref={containerRef}
        height={hasFailed ? height : height - 44}
        isDragging={isDragging}
        hasFailed={hasFailed}
        {...handlers}
      >
        {isLoading && (
          <LoadingOverlay>
            <Typography variant="cap12" color={black[70]}>
              読み込み中...
            </Typography>
          </LoadingOverlay>
        )}
        {hasFailed && (
          <FailureOverlay>
            <OfficeFilePreview filename={src.name} height={height} />
          </FailureOverlay>
        )}
        <ContentWrapper
          style={{
            transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
            transformOrigin: "0 0",
            opacity: isLoading ? 0 : 1,
          }}
        >
          <Box ref={styleRef} sx={{ display: "none" }} />
          <Box ref={contentRef} />
        </ContentWrapper>
      </DocxViewport>
      {!hasFailed && (
        <ZoomControls scale={scale} onZoomIn={zoomIn} onZoomOut={zoomOut} onReset={fitToViewport} />
      )}
    </PreviewContainer>
  );
};

const PreviewContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "width" && prop !== "height" && prop !== "hasFailed",
})<{ width: number | string; height: number; hasFailed: boolean }>`
  width: ${({ width }) => (typeof width === "number" ? `${width}px` : width)};
  height: ${({ height }) => height}px;
  display: flex;
  flex-direction: column;
  margin-top: 16px;
  margin-bottom: 4px;
  border-radius: 8px;
  overflow: hidden;
  background-color: ${background.white};
  border: ${({ hasFailed }) => (hasFailed ? "none" : `1px solid ${black[95]}`)};
`;

const FailureOverlay = styled(Box)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${background.white};
  z-index: 1;
`;

const DocxViewport = styled(Box, {
  shouldForwardProp: (prop) => prop !== "height" && prop !== "isDragging" && prop !== "hasFailed",
})<{ height: number; isDragging: boolean; hasFailed: boolean }>`
  flex: 1;
  overflow: hidden;
  height: ${({ height }) => height}px;
  cursor: ${({ hasFailed, isDragging }) => (hasFailed ? "default" : isDragging ? "grabbing" : "grab")};
  position: relative;
  background-color: ${background.disable};
`;

const ContentWrapper = styled(Box)`
  position: absolute;
  will-change: transform;

  .docx-wrapper {
    background: transparent;
    padding: 0;
  }

  .docx-wrapper > section.docx {
    margin-bottom: 8px;
    box-shadow: 0 0 4px rgba(0, 0, 0, 0.25);
  }

`;

const LoadingOverlay = styled(Box)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${background.disable};
`;
